import React, { useState, useEffect, useMemo } from 'react';
import { Header } from '@/components/Header';
import { SearchBar } from '@/components/SearchBar';
import { ApiGroup } from '@/components/ApiGroup';
import { groupApisByType, filterApis } from '@/utils/apiGrouping';
import { ApiDocumentation, ApiEndpoint } from '@/types/api';
import ApiDataService from '@/services/ApiDataService';

interface ApiDataset {
  id: number;
  name: string;
  data: ApiDocumentation;
}

/**
 * Main application component for the API Documentation Portal
 */
const App: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);
  const [datasets, setDatasets] = useState<ApiDataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Load all API documentation datasets
  useEffect(() => {
    const loadDatasets = async () => {
      try {
        setLoading(true);
        setError(null);
        const collections = await ApiDataService.getAllCollections();
        setDatasets(collections);
      } catch (err) {
        console.error('Error loading API collections:', err);
        setError('Failed to load API collections.');
      } finally {
        setLoading(false);
      }
    };
    
    loadDatasets();
  }, []);
  
  // Combine all APIs from all datasets with dataset info
  const allApis: (ApiEndpoint & { datasetId: number; datasetName: string })[] = useMemo(() => {
    return datasets.flatMap(dataset => 
      dataset.data.apis.map(api => ({
        ...api,
        datasetId: dataset.id,
        datasetName: dataset.name
      }))
    );
  }, [datasets]);
  
  const filteredApis = useMemo(() => {
    let apisToFilter = allApis;
    
    if (selectedCollectionId !== null) {
      apisToFilter = allApis.filter(api => (api as any).datasetId === selectedCollectionId);
    }
    
    return filterApis(apisToFilter, searchQuery);
  }, [allApis, searchQuery, selectedCollectionId]);
  
  // Group filtered APIs by dataset and type
  const datasetGroups = useMemo(() => {
    let datasetsToShow = datasets;
    
    // If a specific collection is selected, only show that one
    if (selectedCollectionId !== null) {
      datasetsToShow = datasets.filter(dataset => dataset.id === selectedCollectionId);
    }
    
    return datasetsToShow.map(dataset => {
      const datasetApis = filteredApis.filter(api => (api as any).datasetId === dataset.id);
      const cleanApis = datasetApis.map(api => {
        const { datasetId, datasetName, ...cleanApi } = api as any;
        return cleanApi as ApiEndpoint;
      });
      const apiGroups = groupApisByType(cleanApis);
      return {
        dataset,
        apiGroups,
        totalApis: datasetApis.length
      };
    }).filter(group => group.totalApis > 0);
  }, [datasets, filteredApis, selectedCollectionId]);
  
  const totalApis = allApis.length;
  const filteredCount = filteredApis.length;
  
  // Calculate the total APIs for the selected collection (for display purposes)
  const selectedCollectionTotalApis = selectedCollectionId 
    ? datasets.find(d => d.id === selectedCollectionId)?.data.apis.length || 0
    : totalApis;
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading API collections...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar Navigation */}
      <div className="w-64 flex-shrink-0 bg-slate-800 shadow-lg">
        <div className="p-6">
          <div className="mb-8">
            <div className="flex justify-center mb-4">
              <svg 
                width="120" 
                height="22" 
                viewBox="0 0 559 100" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-auto"
              >
                <g clipPath="url(#clip0_4687_18)">
                  <path d="M15.3526 14.6702V33.109H45.6127V47.7924H15.3526V77.7923H0V0.0146103H46.5027V14.6813L15.3526 14.6702Z" fill="white"/>
                  <path d="M88.3312 21.124V37.1184C80.8774 36.2295 71.4211 39.5629 71.4211 51.2295V77.7906H57.0698V22.2351H71.4211V31.7851C74.3136 24.224 81.3224 21.124 88.3312 21.124Z" fill="white"/>
                  <path d="M122.377 66.3297C127.94 66.3297 132.39 63.9964 134.838 60.7742L146.408 67.4409C141.179 74.9964 132.835 79.3298 122.155 79.3298C103.465 79.3298 91.6724 66.552 91.6724 49.9964C91.6724 33.4408 103.576 20.6686 121.042 20.6686C137.508 20.6686 149.189 33.6631 149.189 49.9964C149.18 51.972 148.993 53.9428 148.633 55.8853H106.691C108.694 63.2186 114.812 66.3297 122.377 66.3297ZM134.838 44.7742C133.057 36.7742 127.05 33.5575 121.042 33.5575C113.366 33.5575 108.137 37.6631 106.469 44.7742H134.838Z" fill="white"/>
                  <path d="M186.569 66.3297C192.131 66.3297 196.581 63.9964 199.029 60.7742L210.599 67.4409C205.37 74.9964 197.026 79.3298 186.346 79.3298C167.656 79.3298 155.864 66.552 155.864 49.9964C155.864 33.4408 167.768 20.6686 185.234 20.6686C201.699 20.6686 213.38 33.6631 213.38 49.9964C213.371 51.972 213.185 53.9428 212.824 55.8853H170.883C172.885 63.2186 179.004 66.3297 186.569 66.3297ZM199.029 44.7742C197.249 36.7742 191.241 33.5575 185.234 33.5575C177.558 33.5575 172.329 37.6631 170.66 44.7742H199.029Z" fill="white"/>
                  <path d="M263.109 61.552C263.109 73.552 252.651 79.3298 240.748 79.3298C229.623 79.3298 221.39 74.6631 217.385 66.1075L229.845 59.1075C231.403 63.6631 235.185 66.3297 240.748 66.3297C245.309 66.3297 248.424 64.7742 248.424 61.552C248.424 53.4409 219.721 57.8853 219.721 38.3297C219.721 27.002 229.4 20.6686 240.859 20.6686C249.87 20.6686 257.658 24.7797 261.996 32.4464L249.759 39.1131C249.014 37.3868 247.767 35.9234 246.181 34.9122C244.594 33.901 242.74 33.3885 240.859 33.4408C237.299 33.4408 234.406 34.9964 234.406 37.9964C234.406 46.2186 263.109 41.1242 263.109 61.552Z" fill="white"/>
                  <path d="M332.415 50.0131C332.415 66.5687 320.178 79.3464 305.27 79.3464C297.149 79.3464 291.253 76.3465 287.248 71.2354V100.013H272.896V22.2354H287.248V28.7853C291.253 23.6798 297.149 20.6798 305.27 20.6798C320.178 20.6687 332.415 33.4409 332.415 50.0131ZM318.064 50.0131C318.064 40.6798 311.5 34.3465 302.6 34.3465C293.812 34.3465 287.248 40.6798 287.248 50.0131C287.248 59.3465 293.812 65.6798 302.6 65.6798C311.5 65.6631 318.064 59.3298 318.064 50.0131Z" fill="white"/>
                  <path d="M399.165 22.2343V77.7898H384.814V71.2343C380.809 76.2343 374.801 79.3454 366.68 79.3454C351.884 79.3454 339.646 66.5676 339.646 50.012C339.646 33.4565 351.884 20.6843 366.68 20.6843C374.801 20.6843 380.809 23.7898 384.814 28.7898V22.2343H399.165ZM384.814 50.012C384.814 40.6787 378.25 34.3454 369.35 34.3454C360.561 34.3454 353.997 40.6787 353.997 50.012C353.997 59.3454 360.561 65.6787 369.35 65.6787C378.25 65.662 384.814 59.3287 384.814 50.012Z" fill="white"/>
                  <path d="M409.289 50.012C409.289 33.4565 421.749 20.6843 438.659 20.6843C449.562 20.6843 459.018 26.462 463.579 35.012L451.23 42.2343C449.005 37.6787 444.222 34.7898 438.548 34.7898C429.982 34.7898 423.64 41.1231 423.64 50.012C423.64 58.7898 429.982 65.1231 438.548 65.1231C444.333 65.1231 449.117 62.3454 451.342 57.7898L463.802 64.9009C459.018 73.5676 449.562 79.3454 438.659 79.3454C421.749 79.3287 409.289 66.5509 409.289 50.012Z" fill="white"/>
                  <path d="M498.292 66.3297C503.854 66.3297 508.304 63.9964 510.752 60.7742L522.322 67.4409C517.093 74.9964 508.749 79.3298 498.069 79.3298C479.379 79.3298 467.587 66.552 467.587 49.9964C467.587 33.4408 479.49 20.6686 496.957 20.6686C513.422 20.6686 525.103 33.6631 525.103 49.9964C525.094 51.972 524.908 53.9428 524.547 55.8853H482.605C484.608 63.2186 490.727 66.3297 498.292 66.3297ZM510.752 44.7742C508.972 36.7742 502.964 33.5575 496.957 33.5575C489.281 33.5575 484.052 37.6631 482.383 44.7742H510.752Z" fill="white"/>
                  <path d="M535.363 67.7912C535.363 65.4562 536.056 63.1738 537.355 61.2323C538.654 59.2909 540.5 57.7778 542.66 56.8842C544.82 55.9907 547.197 55.7569 549.489 56.2124C551.782 56.668 553.889 57.7923 555.542 59.4434C557.195 61.0944 558.321 63.1979 558.777 65.488C559.233 67.778 558.999 70.1518 558.104 72.3089C557.209 74.4661 555.694 76.3099 553.75 77.6071C551.807 78.9043 549.521 79.5967 547.183 79.5967C545.631 79.5982 544.093 79.2938 542.658 78.701C541.223 78.1083 539.92 77.2387 538.822 76.1422C537.724 75.0456 536.853 73.7436 536.26 72.3107C535.666 70.8777 535.362 69.3419 535.363 67.7912Z" fill="#38D68B"/>
                </g>
                <defs>
                  <clipPath id="clip0_4687_18">
                    <rect width="559" height="100" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className="text-center">
            </div>
          </div>
          
          <nav className="space-y-2">
            {datasets.map((dataset) => (
              <button
                key={dataset.id}
                onClick={() => {
                  // Toggle behavior: if same collection is clicked, deselect it
                  if (selectedCollectionId === dataset.id) {
                    setSelectedCollectionId(null);
                  } else {
                    setSelectedCollectionId(dataset.id);
                  }
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedCollectionId === dataset.id
                    ? 'bg-slate-700 text-white border-l-4 border-emerald-400' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0"></div>
                  <span className="flex-1">{dataset.name}</span>
                  <span className="text-xs text-slate-400 flex-shrink-0">
                    {dataset.data.apis.length}
                  </span>
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <Header documentation={datasets[0]?.data || { title: "API Documentation Portal", description: "Multiple API Collections", apis: [] }} />
      
        <main className="flex-1 px-8 py-6 overflow-x-hidden overflow-y-auto">
        {/* Search and Filter Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search APIs..."
            />
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {filteredCount} of {selectedCollectionTotalApis} APIs
              </span>
              {selectedCollectionId && (
                <span className="text-sm text-emerald-600 font-medium">
                  in {datasets.find(d => d.id === selectedCollectionId)?.name}
                </span>
              )}
              {searchQuery && (
                <span className="text-sm text-gray-500">
                  for "{searchQuery}"
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-600">
              {selectedCollectionId ? '1 collection' : `${datasetGroups.length} collection${datasetGroups.length !== 1 ? 's' : ''}`}
            </span>
            {selectedCollectionId && (
              <button 
                onClick={() => setSelectedCollectionId(null)}
                className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                View All
              </button>
            )}
          </div>
        </div>
        
        {/* API Collections */}
        {datasetGroups.length > 0 ? (
          <div className="space-y-8">
            {datasetGroups.map((datasetGroup) => (
              <div key={datasetGroup.dataset.id} className="bg-white rounded-lg border border-gray-200">
                {/* Collection Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {datasetGroup.dataset.name}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {datasetGroup.dataset.data.description || `Collection ${datasetGroup.dataset.id} API documentation`}
                      </p>
                      {datasetGroup.dataset.data.baseUrl && (
                        <div className="mt-2">
                          <div className="flex flex-col space-y-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Base URLs:</span>
                            <div className="flex flex-wrap gap-3">
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-gray-600">UK:</span>
                                <code className="text-sm font-mono text-blue-700 bg-blue-50 px-2 py-1 rounded">
                                  {datasetGroup.dataset.data.baseUrl}
                                </code>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-gray-600">US:</span>
                                <code className="text-sm font-mono text-blue-700 bg-blue-50 px-2 py-1 rounded">
                                  {(() => {
                                    const baseUrl = datasetGroup.dataset.data.baseUrl;
                                    if (baseUrl.includes('accountws.afreespace.com')) {
                                      return baseUrl.replace('accountws.afreespace.com', 'accountws.us.afreespace.com');
                                    } else if (baseUrl.includes('analytics.afreespace.com')) {
                                      return baseUrl.replace('analytics.afreespace.com', 'analytics.us.afreespace.com');
                                    } else if (baseUrl.includes('spotws.afreespace.com')) {
                                      return baseUrl.replace('spotws.afreespace.com', 'spotws.us.afreespace.com');
                                    } else if (baseUrl.includes('api.spotco.ai')) {
                                      return baseUrl.replace('api.spotco.ai', 'login.us.spotco.ai');
                                    } else if (baseUrl.includes('login.spotco.ai')) {
                                      return baseUrl.replace('login.spotco.ai', 'login.us.spotco.ai');
                                    }
                                    return baseUrl.replace('.afreespace.com', '.us.afreespace.com');
                                  })()}
                                </code>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500">
                        {datasetGroup.totalApis} API{datasetGroup.totalApis !== 1 ? 's' : ''}
                      </span>
                      <span className="text-sm text-gray-500">
                        {datasetGroup.apiGroups.length} group{datasetGroup.apiGroups.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* API Groups within Collection */}
                <div className="p-6">
                  <div className="space-y-4">
                    {datasetGroup.apiGroups.map((group) => (
                      <ApiGroup
                        key={`${datasetGroup.dataset.id}-${group.type}`}
                        group={group}
                        defaultExpanded={false}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-2">
              {searchQuery ? (
                <>No APIs found matching "{searchQuery}"</>
              ) : (
                <>No APIs available</>
              )}
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Clear search
              </button>
            )}
          </div>
        )}
        </main>
      </div>
    </div>
  );
};

export default App;
